// Apolo IDE — UserMenu (avatar + name + Hooks/Plugins/Sair).
//
// Sprint v9.14 — avatar voltou a ser estático (não clicável). Mantém a
// foto pre-loaded do Pedro via useUserAvatar mas só pra display visual.
// Upload de foto removido por solicitação do Pedro.
// 2026-07-07 (Pedro) — clicar no avatar RECOLHE/expande o menu (nome + perfil
// + tema + settings + Sair somem, fica só a foto). Toggle local que compõe com
// o `collapsed` vindo do pai: isCollapsed = collapsed || manualCollapsed.

import { useEffect, useRef, useState } from 'react';
import { BookOpenCheck, Hammer, LogOut, Moon, Package, Palette, Settings2, Sun, User, SlidersHorizontal } from 'lucide-react';
import { useAuthStore } from '../store/useAuthStore.js';
import { useUserAvatar } from '../hooks/useUserAvatar.js';
import { THEMES, useTheme } from '../hooks/useTheme.js';
import HooksView from './HooksView.jsx';
import PluginsView from './PluginsView.jsx';
import AppearanceView from './AppearanceView.jsx';
import AgentRulesView from './AgentRulesView.jsx';
import CustomizeHub from './CustomizeHub.jsx';
import ProfileChip from './ProfileChip.jsx';
import { readCustomizeHubEnabled } from '../lib/flags/sprintFlags.js';

const isElectron = typeof window !== 'undefined' && typeof window.apolo?.app?.rebuild === 'function';

export default function UserMenu({ collapsed = false, workbench = false }) {
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const [hooksOpen, setHooksOpen] = useState(false);
  const [pluginsOpen, setPluginsOpen] = useState(false);
  const [appearanceOpen, setAppearanceOpen] = useState(false);
  const [agentRulesOpen, setAgentRulesOpen] = useState(false);
  const [customizeHubOpen, setCustomizeHubOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [manualCollapsed, setManualCollapsed] = useState(false); // clicar no avatar recolhe/expande
  const customizeHubEnabled = readCustomizeHubEnabled();
  const settingsRef = useRef(null);
  const { avatarUrl } = useUserAvatar(user);
  const { resolved, setTheme } = useTheme();

  // Fecha o drop-up ao clicar fora
  useEffect(() => {
    if (!settingsOpen) return;
    function onClickOutside(e) {
      if (settingsRef.current && !settingsRef.current.contains(e.target)) setSettingsOpen(false);
    }
    const onKey = (event) => { if (event.key === 'Escape') { setSettingsOpen(false); settingsRef.current?.querySelector('button')?.focus(); } };
    document.addEventListener('mousedown', onClickOutside);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onClickOutside); document.removeEventListener('keydown', onKey); };
  }, [settingsOpen]);

  function handleRebuild() {
    setSettingsOpen(false);
    window.dispatchEvent(new CustomEvent('apolo:openActivity', { detail: { section: 'app' } }));
  }
  const isDark = resolved === THEMES.DARK;

  if (!user) return null;

  const initial = (user.name || user.email || '?').charAt(0).toUpperCase();
  const isCollapsed = collapsed || manualCollapsed;

  return (
    <div className={workbench ? "workbench-account-menu" : "flex items-center gap-1 sm:gap-2"}>
      {/* Avatar clicável — recolhe/expande o menu (2026-07-07, Pedro). */}
      <button
        type="button"
        onClick={() => setManualCollapsed((v) => !v)}
        aria-expanded={!isCollapsed}
        aria-label={isCollapsed ? `Expandir menu de ${user.name}` : `Recolher menu de ${user.name}`}
        title={isCollapsed ? 'Expandir menu' : 'Recolher menu'}
        className="flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center overflow-hidden rounded-full bg-apolo-surface2 text-xs font-medium text-apolo-textPrimary transition hover:ring-2 hover:ring-apolo-accent/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-apolo-accent/60"
      >
        {avatarUrl ? (
          <img
            src={avatarUrl}
            alt=""
            className="h-full w-full object-cover"
            draggable={false}
          />
        ) : (
          <>{initial || <User size={12} aria-hidden="true" />}</>
        )}
      </button>
      {/* Recolhido (pelo pai OU pelo click no avatar) = só a foto; nome + perfil
          + tema + settings + Sair somem. O UserStatusBar reexpande no hover. */}
      {!isCollapsed && (
        <>
          <span className={workbench ? "workbench-account-name" : "hidden text-xs text-apolo-textSecondary md:inline"}>{user.name}</span>
          {/* E3 (super-marathon) — profile switcher (flag OFF ⇒ renders nothing). */}
          <ProfileChip />
          {/* Sprint v9.14 — Theme toggle inline (single button morphing Sun↔Moon).
           * Ícone mostra estado atual; click alterna pro oposto. */}
          <button
            type="button"
            aria-label={isDark ? 'Mudar pra tema claro' : 'Mudar pra tema escuro'}
            title={isDark ? 'Tema escuro · click pra claro' : 'Tema claro · click pra escuro'}
            onClick={() => setTheme(isDark ? THEMES.LIGHT : THEMES.DARK)}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-apolo-textTertiary transition hover:bg-apolo-surface1 hover:text-apolo-textPrimary focus:outline-none focus:ring-2 focus:ring-apolo-accent/50"
          >
            {isDark ? <Moon size={14} aria-hidden="true" /> : <Sun size={14} aria-hidden="true" />}
          </button>
          {/* Settings drop-up — agrupa Hooks + Plugins + Rebuild */}
          <div ref={settingsRef} className={workbench ? "workbench-account-settings relative" : "relative hidden md:block"}>
            <button
              type="button"
              aria-label="Configurações"
              title="Configurações"
              onClick={() => setSettingsOpen((v) => !v)}
              className={`inline-flex h-8 w-8 items-center justify-center rounded-md transition focus:outline-none focus:ring-2 focus:ring-apolo-accent/50 ${
                settingsOpen
                  ? 'bg-apolo-surface2 text-apolo-textPrimary'
                  : 'text-apolo-textTertiary hover:bg-apolo-surface1 hover:text-apolo-textPrimary'
              }`}
            >
              <SlidersHorizontal size={14} aria-hidden="true" />
            </button>

            {settingsOpen && (
              <div className="absolute bottom-full left-0 mb-2 min-w-[168px] rounded-lg border border-apolo-borderSubtle bg-apolo-surface1 py-1 shadow-lg">
                {workbench && <button type="button" className="workbench-menu-item" onClick={() => { setSettingsOpen(false); window.dispatchEvent(new CustomEvent('apolo:openActivity', { detail: { section: 'settings' } })); }}><Settings2 size={14} />Modelos e execução</button>}
                {customizeHubEnabled && (
                  <>
                    <button
                      type="button"
                      onClick={() => { setCustomizeHubOpen(true); setSettingsOpen(false); }}
                      className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-xs text-apolo-textPrimary transition hover:bg-apolo-surface2"
                    >
                      <SlidersHorizontal size={12} aria-hidden="true" />
                      Customizar (tudo)
                    </button>
                    <div className="my-1 border-t border-apolo-borderSubtle" />
                  </>
                )}
                <button
                  type="button"
                  onClick={() => { setAppearanceOpen(true); setSettingsOpen(false); }}
                  className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-xs text-apolo-textSecondary transition hover:bg-apolo-surface2 hover:text-apolo-textPrimary"
                >
                  <Palette size={12} aria-hidden="true" />
                  Aparência
                </button>
                <button
                  type="button"
                  onClick={() => { setAgentRulesOpen(true); setSettingsOpen(false); }}
                  className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-xs text-apolo-textSecondary transition hover:bg-apolo-surface2 hover:text-apolo-textPrimary"
                >
                  <BookOpenCheck size={12} aria-hidden="true" />
                  Regras do Agente
                </button>
                <button
                  type="button"
                  onClick={() => { setHooksOpen(true); setSettingsOpen(false); }}
                  className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-xs text-apolo-textSecondary transition hover:bg-apolo-surface2 hover:text-apolo-textPrimary"
                >
                  <Settings2 size={12} aria-hidden="true" />
                  Hooks
                </button>
                <button
                  type="button"
                  onClick={() => { setPluginsOpen(true); setSettingsOpen(false); }}
                  className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-xs text-apolo-textSecondary transition hover:bg-apolo-surface2 hover:text-apolo-textPrimary"
                >
                  <Package size={12} aria-hidden="true" />
                  Plugins
                </button>
                {isElectron && (
                  <>
                    <div className="my-1 border-t border-apolo-borderSubtle" />
                    <button
                      type="button"
                      onClick={handleRebuild}
                      className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-xs text-apolo-warning transition hover:bg-apolo-warningBg/20 hover:text-apolo-highlight disabled:opacity-40"
                    >
                      <Hammer size={12} aria-hidden="true"  />
                      Melhorar Apolo e rebuild
                    </button>
                  </>
                )}
              </div>
            )}
          </div>

          <button
            type="button"
            aria-label="Sair"
            onClick={logout}
            className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-apolo-textTertiary transition hover:bg-apolo-surface1 hover:text-apolo-error focus:outline-none focus:ring-2 focus:ring-apolo-accent/50 lg:h-8 lg:w-8"
          >
            <LogOut size={16} className="lg:h-[14px] lg:w-[14px]" aria-hidden="true" />
          </button>
        </>
      )}
      <HooksView open={hooksOpen} onClose={() => setHooksOpen(false)} />
      <PluginsView open={pluginsOpen} onClose={() => setPluginsOpen(false)} />
      <AppearanceView open={appearanceOpen} onClose={() => setAppearanceOpen(false)} />
      <AgentRulesView open={agentRulesOpen} onClose={() => setAgentRulesOpen(false)} />
      {customizeHubEnabled && <CustomizeHub open={customizeHubOpen} onClose={() => setCustomizeHubOpen(false)} />}
    </div>
  );
}
